"use client";

import {createModal} from "@gluestack-ui/modal";
import type {VariantProps} from "@gluestack-ui/nativewind-utils";
import {tva} from "@gluestack-ui/nativewind-utils/tva";
import {useStyleContext, withStyleContext} from "@gluestack-ui/nativewind-utils/withStyleContext";
import {AnimatePresence, createMotionAnimatedComponent, Motion} from "@legendapp/motion";
import {cssInterop} from "nativewind";
import type {ComponentType} from "react";
import React from "react";
import {Pressable, ScrollView, View} from "react-native";

const AnimatedPressable = createMotionAnimatedComponent(Pressable);
const SCOPE = "MODAL";
const Root = withStyleContext(View, SCOPE);
const UIModal = createModal({
    Root,
    // @ts-expect-error
    Backdrop: AnimatedPressable,
    // @ts-expect-error
    Content: Motion.View,
    Body: ScrollView,
    CloseButton: Pressable,
    Footer: View,
    Header: View,
    AnimatePresence
});

cssInterop(
    AnimatedPressable as ComponentType<{
        style: unknown;
    }>,
    {className: "style"}
);
cssInterop(
    Motion.View as ComponentType<{
        style: unknown;
    }>,
    {className: "style"}
);

const modalStyle = tva({
    base: "group/modal w-full h-full justify-center items-center web:pointer-events-none",
    variants: {
        size: {
            xs: "",
            sm: "",
            md: "",
            lg: "",
            xl: "",
            full: ""
        }
    }
});

const modalBackdropStyle = tva({
    base: "absolute left-0 top-0 right-0 bottom-0 bg-background-dark web:cursor-default"
});

const modalContentStyle = tva({
    base:
        "bg-white " +
        "border " +
        "border-outline-100 " +
        "shadow-hard-2 " +
        "rounded-xl " +
        "overflow-hidden " +
        "max-h-[min(769px,95%)] ",

    parentVariants: {
        size: {
            xs: "w-[60%] max-w-[280px]",
            sm: "w-[65%] max-w-[320px]",
            md: "w-[75%] max-w-[640px]",
            lg: "w-[80%] max-w-[800px]",
            xl: "w-[90%] max-w-[1000px]",
            full: "w-full"
        }
    }
});

const modalBodyStyle = tva({
    base: "pt-0 pb-5 px-5",
    variants: {
        hasFooter: {
            true: "pb-4"
        }
    }
});

const modalCloseButtonStyle = tva({
    base: "group/modal-close-button z-10 rounded data-[focus-visible=true]:web:bg-background-100 web:outline-0 cursor-pointer"
});

const modalHeaderStyle = tva({
    base: "justify-between items-center flex-row pb-4 pt-5 px-5",
    variants: {
        hasFooter: {
            true: "pt-4"
        }
    }
});

const modalFooterStyle = tva({
    base: "flex-row justify-end items-center gap-2 px-5 py-4 mt-0 border-t border-t-solid border-t-blueGray-200",
    variants: {
        hasChildren: {
            true: "mt-2"
        }
    }
});

export type IModalProps = React.ComponentProps<typeof UIModal> &
    VariantProps<typeof modalStyle> & {className?: string};

type IModalBackdropProps = React.ComponentProps<typeof UIModal.Backdrop> &
    VariantProps<typeof modalBackdropStyle> & {className?: string};

type IModalContentProps = React.ComponentProps<typeof UIModal.Content> &
    VariantProps<typeof modalContentStyle> & {className?: string};

type IModalHeaderProps = React.ComponentProps<typeof UIModal.Header> &
    VariantProps<typeof modalHeaderStyle> & {className?: string} & {
        hasFooter?: boolean;
    };

type IModalBodyProps = React.ComponentProps<typeof UIModal.Body> &
    VariantProps<typeof modalBodyStyle> & {className?: string} & {
        hasFooter?: boolean;
    };

type IModalFooterProps = React.ComponentProps<typeof UIModal.Footer> &
    VariantProps<typeof modalFooterStyle> & {className?: string} & {
        hasChildren?: boolean;
    };

type IModalCloseButtonProps = React.ComponentProps<typeof UIModal.CloseButton> &
    VariantProps<typeof modalCloseButtonStyle> & {className?: string};

const ModalUI = React.forwardRef<React.ComponentRef<typeof UIModal>, IModalProps>(
    ({className, size = "md", ...props}, ref) => (
        <UIModal
            ref={ref}
            testID="modal-wrapper"
            {...props}
            className={modalStyle({size, class: className})}
            context={{size}}
            pointerEvents="box-none"
        />
    )
);

const ModalBackdrop = React.forwardRef<
    React.ElementRef<typeof UIModal.Backdrop>,
    IModalBackdropProps
>(({className, ...props}, ref) => {
    return (
        <UIModal.Backdrop
            ref={ref}
            animate={{
                opacity: 0.5
            }}
            exit={{
                opacity: 0
            }}
            initial={{
                opacity: 0
            }}
            testID="modal-backdrop"
            transition={{
                type: "spring",
                damping: 18,
                stiffness: 250,
                opacity: {
                    type: "timing",
                    duration: 250
                }
            }}
            {...props}
            className={modalBackdropStyle({
                class: className
            })}
        />
    );
});

const ModalContent = React.forwardRef<React.ElementRef<typeof UIModal.Content>, IModalContentProps>(
    ({className, size, ...props}, ref) => {
        const {size: parentSize} = useStyleContext(SCOPE);

        return (
            <UIModal.Content
                ref={ref}
                animate={{
                    opacity: 1,
                    scale: 1
                }}
                exit={{
                    opacity: 0
                }}
                initial={{
                    opacity: 0,
                    scale: 0.9
                }}
                transition={{
                    type: "spring",
                    damping: 18,
                    stiffness: 250,
                    opacity: {
                        type: "timing",
                        duration: 250
                    }
                }}
                {...props}
                className={modalContentStyle({
                    parentVariants: {
                        size: parentSize
                    },
                    size,
                    class: className
                })}
                pointerEvents="auto"
            />
        );
    }
);

const ModalHeaderUI = React.forwardRef<React.ElementRef<typeof UIModal.Header>, IModalHeaderProps>(
    ({className, hasFooter, ...props}, ref) => {
        return (
            <UIModal.Header
                ref={ref}
                {...props}
                className={modalHeaderStyle({
                    class: className,
                    hasFooter
                })}
            />
        );
    }
);

const ModalBodyUI = React.forwardRef<React.ElementRef<typeof UIModal.Body>, IModalBodyProps>(
    ({className, ...props}, ref) => {
        return (
            <UIModal.Body
                ref={ref}
                {...props}
                className={modalBodyStyle({
                    class: className
                })}
            />
        );
    }
);

const ModalFooterUI = React.forwardRef<React.ElementRef<typeof UIModal.Footer>, IModalFooterProps>(
    ({className, hasChildren, ...props}, ref) => {
        return (
            <UIModal.Footer
                ref={ref}
                {...props}
                className={modalFooterStyle({
                    class: className,
                    hasChildren
                })}
            />
        );
    }
);

const ModalCloseButton = React.forwardRef<
    React.ElementRef<typeof UIModal.CloseButton>,
    IModalCloseButtonProps
>(({className, ...props}, ref) => {
    return (
        <UIModal.CloseButton
            ref={ref}
            {...props}
            className={modalCloseButtonStyle({
                class: className
            })}
        />
    );
});

ModalUI.displayName = "Modal";
ModalBackdrop.displayName = "ModalBackdrop";
ModalContent.displayName = "ModalContent";
ModalHeaderUI.displayName = "ModalHeader";
ModalBodyUI.displayName = "ModalBody";
ModalFooterUI.displayName = "ModalFooter";
ModalCloseButton.displayName = "ModalCloseButton";

export {
    ModalUI,
    ModalBackdrop,
    ModalContent,
    ModalCloseButton,
    ModalHeaderUI,
    ModalBodyUI,
    ModalFooterUI
};
